import React, { useState, useRef, useEffect, useCallback, Suspense, lazy } from 'react';
import { Sun, Moon, Bell, ChevronDown, ChevronLeft, ChevronRight, Settings, LogOut, CreditCard, Zap, HelpCircle, Shield, Check, X, Eye, Play, Send } from 'lucide-react';
import V4Sidebar from './v4/V4Sidebar';
import V4ChatPanel from './v4/V4ChatPanel';
import V4DailyTasksPanel from './v4/V4DailyTasksPanel';
// Lazy-loaded — only mount when their nav state activates. Saves initial bundle.
const V4OrgPulse = lazy(() => import('./v4/V4OrgPulse'));
const V4History = lazy(() => import('./v4/V4History'));
const V4Integrations = lazy(() => import('./v4/V4Integrations'));
const V4KnowledgeVault = lazy(() => import('./v4/V4KnowledgeVault'));
import V4MarketingDept from './v4/V4MarketingDept';
import V4MktCampaigns from './v4/V4MktCampaigns';
import V4MktSocial from './v4/V4MktSocial';
import V4MktContent from './v4/V4MktContent';
import V4MktAnalytics from './v4/V4MktAnalytics';
import V4SalesDept from './v4/V4SalesDept';
import V4SalesDeals from './v4/V4SalesDeals';
import V4SalesPipeline from './v4/V4SalesPipeline';
import V4SalesOutreach from './v4/V4SalesOutreach';
import V4SalesReports from './v4/V4SalesReports';
import V4SupportDept from './v4/V4SupportDept';
import V4SupportTickets from './v4/V4SupportTickets';
import V4SupportChurn from './v4/V4SupportChurn';
import V4SupportSentiment from './v4/V4SupportSentiment';
import V4PeopleDept from './v4/V4PeopleDept';
import V4PeopleTeam from './v4/V4PeopleTeam';
import V4PeopleHiring from './v4/V4PeopleHiring';
import V4PeopleEngagement from './v4/V4PeopleEngagement';
import V4PeopleOrgHealth from './v4/V4PeopleOrgHealth';
import V4FinanceDept from './v4/V4FinanceDept';
import V4FinanceCashflow from './v4/V4FinanceCashflow';
import V4FinanceBudgets from './v4/V4FinanceBudgets';
import V4FinanceInvoices from './v4/V4FinanceInvoices';
import V4FinanceReports from './v4/V4FinanceReports';
import V4WorkflowsList from './v4/V4WorkflowsList';
const V4WorkflowDetail = lazy(() => import('./v4/V4WorkflowDetail'));
const V4WorkflowBuilder = lazy(() => import('./v4/V4WorkflowBuilder'));
import V4AgentsList from './v4/V4AgentsList';
const V4AgentDetail = lazy(() => import('./v4/V4AgentDetail'));
import V4AdHocChat from './v4/V4AdHocChat';
const V4WorkflowExecution = lazy(() => import('./v4/V4WorkflowExecution'));
import V4CheatSheet from './v4/V4CheatSheet';
import V4OnboardingTour, { shouldAutoOpenTour } from './v4/V4OnboardingTour';
import MARKETING_SCENARIOS from '../data/marketingAdHocData';
import PRIYA_HOME_SCENARIOS from '../data/priyaHomeAdHocData';
import DAVID_HOME_SCENARIOS from '../data/davidHomeAdHocData';
import PRIYA_CAMPAIGN_PORTFOLIO_SCENARIOS from '../data/priyaCampaignPortfolioData';
import SALES_SCENARIOS from '../data/salesAdHocData';
import SUPPORT_SCENARIOS from '../data/supportAdHocData';
import FINANCE_SCENARIOS from '../data/financeAdHocData';
import PEOPLE_SCENARIOS from '../data/peopleAdHocData';
import SCENARIOS from '../data/adHocChatData';
import PRIYA_SOCIAL_KEYPRESS from '../data/priyaSocialKeypressData';
import { PRIYA_CONTENT_KEYPRESS } from '../data/priyaContentDeepDiveData';
import { PRIYA_ANALYTICS_KEYPRESS } from '../data/priyaAnalyticsDeepDiveData';
import { DAVID_DEALS_KEYPRESS } from '../data/davidDealsCreateKeypressData';
import { DAVID_PIPELINE_KEYPRESS } from '../data/davidPipelineDDData';
import { DAVID_OUTREACH_KEYPRESS } from '../data/davidOutreachDDData';
import { DAVID_REPORTS_KEYPRESS } from '../data/davidReportsDDData';
import { getFirstUserMsg } from '../hooks/scenarioIntroHelper';
import { AGENT_THUMBS } from './v4/V4BriefingShared';
import useSimClock from '../hooks/useSimClock';
import useSimState from '../hooks/useSimState';
import { setRippleHandler, setRippleFromAccount } from '../hooks/rippleBus';
import { ChatInputRegistryProvider, useChatIntro } from '../hooks/useChatInputRegistry';
import ToastSystem from '../components/ToastSystem';
import NotificationDropdown from '../components/NotificationDropdown';
import { trackEvent, trackPageView } from '../lib/analytics';

const AGENT_NAMES = { stark: 'Stark', luc: 'Luc', marsi: 'Marsi', sally: 'Sally', zira: 'Zira', tali: 'Tali', finn: 'Finn', dora: 'Dora', buzz: 'Buzz' };

const MOCK_RESPONSES = {
  stark: "I've analyzed the data across all departments. Here's what I'm seeing — the v2.3 release is the primary driver of the health score drop. Support is the most impacted (SLA compliance at 74%), which is cascading into churn risk for 5 accounts worth $89K ARR. Engineering has a hotfix in progress (ETA: 2 hours). Once that's deployed, I expect the operational score to recover within 48 hours. The revenue impact from TechCorp and DataFlow is more structural — I'd recommend the executive outreach path for TechCorp.",
};

const ContextChatPanel = ({ isDark, agentId, messages, onClose, onSend }) => {
  const [input, setInput] = useState('');
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/40' : 'text-slate-500';
  const divider = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const inputBg = isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]';
  const agentThumb = AGENT_THUMBS[agentId];
  const agentName = AGENT_NAMES[agentId] || agentId;
  const mockReply = MOCK_RESPONSES[agentId] || `I'm looking into that for you. Let me cross-reference the latest data across all relevant systems and get back to you with a full analysis.`;

  const handleSend = () => {
    if (input.trim()) {
      onSend(input.trim());
      setInput('');
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="context-chat-panel">
      <div className={`flex items-center justify-between px-4 py-4 border-b ${divider}`}>
        <div className="flex items-center gap-2">
          {agentThumb && <img src={agentThumb} alt="" className="w-6 h-6 rounded-full object-cover" />}
          <div>
            <p className={`text-sm font-semibold ${textPrimary}`}>{agentName}</p>
            <p className={`text-[10px] ${textSecondary}`}>Context chat</p>
          </div>
        </div>
        <button onClick={onClose} className={`p-1 rounded-lg ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'}`} data-testid="context-chat-close">
          <X className={`w-4 h-4 ${textSecondary}`} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.map((msg, i) => (
          <React.Fragment key={i}>
            {msg.role === 'user' && (
              <div className="flex justify-end">
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-[11px] leading-relaxed ${isDark ? 'bg-indigo-500/20 text-white' : 'bg-indigo-500/15 text-slate-800'}`}>
                  {msg.text}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              {agentThumb && <img src={agentThumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0 mt-0.5" />}
              <div className={`max-w-[90%] px-3 py-2 rounded-lg text-[11px] leading-relaxed ${isDark ? 'bg-white/[0.04] text-white/70' : 'bg-black/[0.03] text-slate-600'}`}>
                {mockReply}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      <div className={`px-3 py-3 border-t ${divider}`}>
        <div className={`flex items-center gap-2 rounded-lg ${inputBg} px-3 py-2`}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={`Ask ${agentName}...`}
            className={`flex-1 bg-transparent outline-none text-xs ${textPrimary}`}
            data-testid="context-chat-input"
          />
          <button onClick={handleSend} disabled={!input.trim()} className={`p-1 rounded ${input.trim() ? (isDark ? 'text-indigo-400' : 'text-indigo-600') : 'opacity-30'}`} data-testid="context-chat-send">
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const USERS = [
  { id: 'ceo', name: 'Arjun Mehta', role: 'CEO', initials: 'AM', gradient: 'from-indigo-400 to-purple-500' },
  { id: 'marketing', name: 'Priya Sharma', role: 'Marketing Lead', initials: 'PS', gradient: 'from-rose-400 to-pink-500' },
  { id: 'sales', name: 'David Chen', role: 'Sales Director', initials: 'DC', gradient: 'from-amber-400 to-orange-500' },
  { id: 'support', name: 'Maya Johnson', role: 'Support Manager', initials: 'MJ', gradient: 'from-emerald-400 to-teal-500' },
  { id: 'people', name: 'Riya Patel', role: 'People Manager', initials: 'RP', gradient: 'from-fuchsia-400 to-pink-600' },
  { id: 'finance', name: 'Neel Kapoor', role: 'Finance Manager', initials: 'NK', gradient: 'from-green-400 to-emerald-600' },
];

const SAMPLE_WORKFLOWS = [
  { id: 'weekly-recap', name: 'Weekly Recap', agent: 'Luc', description: 'Activity snapshot, insights, and next week guidance' },
  { id: 'growth-plan', name: 'Personal Growth Plan', agent: 'Luc', description: 'Personalized leadership development with weekly check-ins' },
  { id: 'meta-campaign', name: 'Meta Campaign Builder', agent: 'Marsi', description: 'Create and launch Meta ad campaigns with governed execution' },
  { id: 'outbound', name: 'Outbound Distribution', agent: 'Marsi', description: 'Email + LinkedIn DM outreach at scale' },
  { id: 'proposal', name: 'Proposal Builder', agent: 'Sally', description: 'Client-ready proposals from CRM data in under 10 min' },
  { id: 'lead-qualify', name: 'Lead Qualification', agent: 'Sally', description: 'Score, enrich, and tag leads automatically' },
  { id: 'sales-followup', name: 'Sales Follow-up', agent: 'Sally', description: 'Deal monitoring with next-best-action recommendations' },
  { id: 'daily-plan', name: 'Daily Plan', agent: 'Luc', description: 'Morning brief with prioritized tasks' },
  { id: 'action-tracker', name: 'Action Tracker', agent: 'Luc', description: 'Track replies, follow-ups, and decisions' },
  { id: 'org-health-audit', name: 'Org Health Audit', agent: 'Stark', description: 'Cross-department health check with risk prioritization' },
  { id: 'executive-brief', name: 'Executive Brief', agent: 'Stark', description: 'Board-ready summary of KPIs, risks, and strategic initiatives' },
  { id: 'dept-benchmark', name: 'Department Benchmark', agent: 'Stark', description: 'Compare department performance against targets and historical trends' },
  { id: 'risk-cascade', name: 'Risk Cascade Analysis', agent: 'Stark', description: 'Map how a single risk propagates across departments and revenue' },
];

// Per-account visibility — Priya sees Luc + marketing agents only; David sees Luc + Sally only
const PRIYA_VISIBLE_AGENTS = new Set(['Luc', 'Marsi', 'Wright', 'Buzz', 'Dora']);
const DAVID_VISIBLE_AGENTS = new Set(['Luc', 'Sally']);

const WorkflowColumn = ({ isDark, onClose, agentFilter, onUse, activeAccount }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/40' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const cardBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.06]';
  const divider = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  const cardBg = isDark ? '' : 'bg-[#f0ece4]';
  const accountFiltered = activeAccount === 'marketing'
    ? SAMPLE_WORKFLOWS.filter(wf => PRIYA_VISIBLE_AGENTS.has(wf.agent))
    : activeAccount === 'sales'
      ? SAMPLE_WORKFLOWS.filter(wf => DAVID_VISIBLE_AGENTS.has(wf.agent))
      : SAMPLE_WORKFLOWS;
  const workflows = agentFilter ? accountFiltered.filter(wf => wf.agent === agentFilter) : accountFiltered;

  return (
    <div className="flex flex-col h-full">
      <div className={`flex items-center justify-between px-4 py-4 border-b ${divider}`}>
        <p className={`text-sm font-semibold ${textPrimary}`}>Workflows</p>
        <button onClick={onClose} className={`p-1 rounded-lg transition-colors ${hoverBg}`} data-testid="v4-workflow-close">
          <X className={`w-4 h-4 ${textSecondary}`} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-3">
        {workflows.map((wf) => (
          <div key={wf.id} className={`p-3 rounded-xl border ${cardBorder} ${cardBg} ${hoverBg} flex-shrink-0`} data-testid={`v4-workflow-${wf.id}`}>
            <div className="flex items-center justify-between mb-1">
              <p className={`text-xs font-semibold ${textPrimary}`}>{wf.name}</p>
            </div>
            <p className={`text-[11px] leading-relaxed ${textSecondary} mb-2`}>{wf.description}</p>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-white/[0.06] text-white/50' : 'bg-black/[0.04] text-slate-500'}`}>{wf.agent}</span>
            <div className="flex items-center gap-2 mt-2">
              <button onClick={() => onUse?.(wf.id)} className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-[11px] font-medium" data-testid={`v4-workflow-use-${wf.id}`}>
                <Play className="w-3 h-3" /> Use
              </button>
              <button className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium border ${cardBorder} ${textSecondary} ${hoverBg}`} data-testid={`v4-workflow-view-${wf.id}`}>
                <Eye className="w-3 h-3" /> View
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const V4WrapperInner = () => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('implify-theme');
    return saved ? saved === 'dark' : true;
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountsExpanded, setAccountsExpanded] = useState(false);
  const [userIndex, setUserIndex] = useState(0);
  const [activeNav, setActiveNav] = useState('home');
  const [workflowPanelOpen, setWorkflowPanelOpen] = useState(false);
  const [dailyTasksPanelOpen, setDailyTasksPanelOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [contextChatOpen, setContextChatOpen] = useState(false);
  const [contextChatAgent, setContextChatAgent] = useState(null);
  const [contextChatMessages, setContextChatMessages] = useState([]);
  const [kvPanelOpen, setKvPanelOpen] = useState(false);
  const [kvPanelMode, setKvPanelMode] = useState(null); // 'detail' | 'add'
  const [kvPanelContent, setKvPanelContent] = useState(null); // content element
  const [intPanelOpen, setIntPanelOpen] = useState(false);
  const [intPanelContent, setIntPanelContent] = useState(null);
  const [navContext, setNavContext] = useState(null);
  const [executionState, setExecutionState] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [activeExecIndex, setActiveExecIndex] = useState(0);
  const [shouldResumeExecution, setShouldResumeExecution] = useState(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [adHocScenario, setAdHocScenario] = useState(null);
  const [marketingAdHocScenario, setMarketingAdHocScenario] = useState(null);
  const [salesAdHocScenario, setSalesAdHocScenario] = useState(null);
  const [supportAdHocScenario, setSupportAdHocScenario] = useState(null);
  const [financeAdHocScenario, setFinanceAdHocScenario] = useState(null);
  const [peopleAdHocScenario, setPeopleAdHocScenario] = useState(null);
  const [activeWorkflowExec, setActiveWorkflowExec] = useState(null);
  const menuRef = useRef(null);
  const activeUser = USERS[userIndex];

  // GA4 page_view on internal nav / persona change
  useEffect(() => {
    trackPageView({
      path: `/v4/${activeNav}`,
      title: `Implify · ${activeUser.name} · ${activeNav}`,
    });
  }, [activeNav, activeUser.id]);

  // Simulation
  const simClock = useSimClock();
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((toast) => {
    setToasts(prev => [...prev, { ...toast, id: Date.now() + Math.random() }]);
  }, []);
  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  const sim = useSimState(simClock.totalMinutes, addToast, simClock.cycle);
  const [notifOpen, setNotifOpen] = useState(false);
  const [cheatOpen, setCheatOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const { playIntro, isIntroActive } = useChatIntro();

  // Auto-open onboarding tour on first ever load (CEO + home only)
  useEffect(() => {
    if (activeUser?.id !== 'ceo' || activeNav !== 'home') return;
    if (!shouldAutoOpenTour()) return;
    // Wait one paint so target rects exist
    const t = setTimeout(() => setTourOpen(true), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for global "open tour" event (replay link from cheat sheet)
  useEffect(() => {
    const handler = () => {
      trackEvent('tour_replayed');
      setCheatOpen(false);
      setTourOpen(true);
    };
    window.addEventListener('implify:open-tour', handler);
    return () => window.removeEventListener('implify:open-tour', handler);
  }, []);

  // Listen for tour-step-change so we can open the persona menu when the
  // tour reaches that step (and close it when it leaves).
  useEffect(() => {
    const handler = (e) => {
      const stepId = e?.detail?.stepId;
      if (stepId === 'persona-switcher') {
        setMenuOpen(true);
        setAccountsExpanded(true);
        setNotifOpen(false);
      } else {
        // Any other step (or tour close) — collapse the menu if it was opened by the tour
        setMenuOpen(false);
        setAccountsExpanded(false);
      }
    };
    window.addEventListener('implify:tour-step-change', handler);
    return () => window.removeEventListener('implify:tour-step-change', handler);
  }, []);

  // Wire ripple bus to sim.addRipple + keep the "from" account in sync with active persona
  useEffect(() => {
    setRippleHandler(sim.addRipple);
    return () => setRippleHandler(null);
  }, [sim.addRipple]);
  useEffect(() => {
    const uid = activeUser?.id;
    const fromAccount = uid === 'ceo' ? 'arjun' : uid === 'marketing' ? 'priya' : uid === 'sales' ? 'david' : uid || 'arjun';
    setRippleFromAccount(fromAccount);
  }, [activeUser?.id]);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setAccountsExpanded(false);
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Ad-hoc chat: A/B/C keypress triggers scenarios
  //   - CEO on home: A/B/C
  //   - Priya on home: A/B
  //   - Priya on mkt-campaigns: A only (portfolio review)
  //   - David on home: A/B
  useEffect(() => {
    const handleKeyDown = (e) => {
      const uid = activeUser?.id;
      if (!(uid === 'ceo' || uid === 'marketing' || uid === 'sales')) return;
      if (adHocScenario || isIntroActive) return;
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || document.activeElement?.isContentEditable) return;
      const key = e.key.toLowerCase();

      // Determine which keys are allowed for current (user, nav) combination
      let allowed = [];
      if (activeNav === 'home') {
        allowed = uid === 'ceo' ? ['a', 'b', 'c'] : ['a', 'b'];
      } else if (activeNav === 'mkt-campaigns' && uid === 'marketing') {
        allowed = ['a'];
      } else if (activeNav === 'mkt-social' && uid === 'marketing') {
        allowed = ['a', 'b'];
      } else if (activeNav === 'mkt-content' && uid === 'marketing') {
        allowed = ['a'];
      } else if (activeNav === 'mkt-analytics' && uid === 'marketing') {
        allowed = ['a'];
      } else if (activeNav === 'sales-deals' && uid === 'sales') {
        allowed = ['a'];
      } else if (activeNav === 'sales-pipeline' && uid === 'sales') {
        allowed = ['a'];
      } else if (activeNav === 'sales-outreach' && uid === 'sales') {
        allowed = ['a'];
      } else if (activeNav === 'sales-reports' && uid === 'sales') {
        allowed = ['a'];
      }
      if (allowed.length === 0) return;

      if (allowed.includes(key)) {
        e.preventDefault();
        // Close side panels before intro
        setWorkflowPanelOpen(false);
        setDailyTasksPanelOpen(false);
        setContextChatOpen(false);

        // Determine which scenarios dict to pull the first user-msg from (for typing intro)
        let scenariosDict = null;
        if (activeNav === 'home') {
          scenariosDict = uid === 'ceo' ? SCENARIOS : uid === 'marketing' ? PRIYA_HOME_SCENARIOS : DAVID_HOME_SCENARIOS;
        } else if (activeNav === 'mkt-campaigns') {
          scenariosDict = PRIYA_CAMPAIGN_PORTFOLIO_SCENARIOS;
        } else if (activeNav === 'mkt-social') {
          scenariosDict = PRIYA_SOCIAL_KEYPRESS;
        } else if (activeNav === 'mkt-content') {
          scenariosDict = PRIYA_CONTENT_KEYPRESS;
        } else if (activeNav === 'mkt-analytics') {
          scenariosDict = PRIYA_ANALYTICS_KEYPRESS;
        } else if (activeNav === 'sales-deals') {
          scenariosDict = DAVID_DEALS_KEYPRESS;
        } else if (activeNav === 'sales-pipeline') {
          scenariosDict = DAVID_PIPELINE_KEYPRESS;
        } else if (activeNav === 'sales-outreach') {
          scenariosDict = DAVID_OUTREACH_KEYPRESS;
        } else if (activeNav === 'sales-reports') {
          scenariosDict = DAVID_REPORTS_KEYPRESS;
        }

        const introText = scenariosDict ? getFirstUserMsg(scenariosDict, key) : '';
        trackEvent('scenario_triggered', { persona: uid, nav: activeNav, key });
        playIntro(introText, () => setAdHocScenario(key));
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeUser?.id, activeNav, adHocScenario, isIntroActive, playIntro]);

  // Marketing keypress — A/B triggers Marketing scenarios
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeUser?.id !== 'ceo' || activeNav !== 'marketing' || marketingAdHocScenario || isIntroActive) return;
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || document.activeElement?.isContentEditable) return;
      const key = e.key.toLowerCase();
      if (key === 'a' || key === 'b') {
        e.preventDefault();
        setWorkflowPanelOpen(false);
        setDailyTasksPanelOpen(false);
        setContextChatOpen(false);
        const introText = getFirstUserMsg(MARKETING_SCENARIOS, key);
        trackEvent('scenario_triggered', { persona: 'ceo', nav: 'marketing', key });
        playIntro(introText, () => setMarketingAdHocScenario(key));
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeUser?.id, activeNav, marketingAdHocScenario, isIntroActive, playIntro]);

  // Sales / Support / Finance / People keypress — A/B triggers dept scenarios
  useEffect(() => {
    const deptMap = {
      sales: [salesAdHocScenario, setSalesAdHocScenario, SALES_SCENARIOS],
      support: [supportAdHocScenario, setSupportAdHocScenario, SUPPORT_SCENARIOS],
      finance: [financeAdHocScenario, setFinanceAdHocScenario, FINANCE_SCENARIOS],
      people: [peopleAdHocScenario, setPeopleAdHocScenario, PEOPLE_SCENARIOS],
    };
    const entry = deptMap[activeNav];
    const handleKeyDown = (e) => {
      if (activeUser?.id !== 'ceo' || !entry || entry[0] || isIntroActive) return;
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || document.activeElement?.isContentEditable) return;
      const key = e.key.toLowerCase();
      if (key === 'a' || key === 'b') {
        e.preventDefault();
        setWorkflowPanelOpen(false);
        setDailyTasksPanelOpen(false);
        setContextChatOpen(false);
        const introText = getFirstUserMsg(entry[2], key);
        trackEvent('scenario_triggered', { persona: 'ceo', nav: activeNav, key });
        playIntro(introText, () => entry[1](key));
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeUser?.id, activeNav, salesAdHocScenario, supportAdHocScenario, financeAdHocScenario, peopleAdHocScenario, isIntroActive, playIntro]);

  // Global `?` keypress toggles the cheat sheet (skips when typing into inputs)
  useEffect(() => {
    const handler = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || document.activeElement?.isContentEditable) return;
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setCheatOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const bg = isDark ? 'aurora-gradient' : 'aurora-gradient-light';
  const glass = isDark ? 'v4-glass' : 'v4-glass-light';
  const iconColor = isDark ? 'text-white/60 hover:text-white' : 'text-slate-500 hover:text-slate-800';
  const logo = isDark ? '/implify-logo-white.png' : '/implify-logo.png';
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const divider = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  const switchAccount = (idx) => {
    trackEvent('persona_switch', { from: activeUser?.id, to: USERS[idx]?.id });
    setUserIndex(idx);
    setAccountsExpanded(false);
    setMenuOpen(false);
    setActiveNav('home');
    setWorkflowPanelOpen(false);
    setDailyTasksPanelOpen(false);
    setContextChatOpen(false);
    setContextChatMessages([]);
    setAdHocScenario(null);
    setMarketingAdHocScenario(null);
    setSalesAdHocScenario(null);
    setSupportAdHocScenario(null);
    setFinanceAdHocScenario(null);
    setPeopleAdHocScenario(null);
    setActiveWorkflowExec(null);
  };

  const toggleWorkflow = () => {
    setWorkflowPanelOpen(prev => !prev);
    setDailyTasksPanelOpen(false);
    setContextChatOpen(false);
  };

  const toggleDailyTasks = () => {
    setDailyTasksPanelOpen(prev => !prev);
    setWorkflowPanelOpen(false);
    setContextChatOpen(false);
  };

  const handleNavChange = (navId) => {
    setActiveNav(navId);
    setWorkflowPanelOpen(false);
    setDailyTasksPanelOpen(false);
    setContextChatOpen(false);
    setContextChatMessages([]);
    setKvPanelOpen(false);
    setKvPanelContent(null);
    setIntPanelOpen(false);
    setIntPanelContent(null);
    setNavContext(null);
    setNotifOpen(false);
    setAdHocScenario(null);
    setMarketingAdHocScenario(null);
    setSalesAdHocScenario(null);
    setSupportAdHocScenario(null);
    setFinanceAdHocScenario(null);
    setPeopleAdHocScenario(null);
    setActiveWorkflowExec(null);
  };

  const handleNavigateWithContext = (navId, context) => {
    setNavContext(context || null);
    handleNavChange(navId);
    // Re-set context after handleNavChange clears it
    if (context) setTimeout(() => setNavContext(context), 0);
  };

  const handleContextChat = (message, agentId) => {
    setContextChatAgent(agentId);
    setContextChatMessages(prev => [...prev, { role: 'user', text: message }]);
    setContextChatOpen(true);
    setWorkflowPanelOpen(false);
    setDailyTasksPanelOpen(false);
  };

  const handleExecutionChange = (state) => {
    setExecutionState(state);
    setExecutions(prev => {
      if (prev.length === 0 && state.active) {
        return [
          { id: 'xr1', ...state },
          { id: 'xr2', title: 'Monitoring: Engineering Capacity', completedSteps: 14, totalSteps: 32, hitlPending: false, active: true, inView: false },
          { id: 'xr3', title: 'Tracking: TechCorp Account Risk', completedSteps: 6, totalSteps: 18, hitlPending: true, active: true, inView: false },
        ];
      }
      if (prev.length > 0) {
        const updated = [...prev];
        updated[0] = { ...updated[0], ...state };
        return updated;
      }
      return prev;
    });
  };

  const handleStatusBarClick = () => {
    if (activeNav === 'org-pulse') {
      setShouldResumeExecution(true);
    } else {
      setActiveNav('org-pulse');
      setShouldResumeExecution(true);
    }
    setWorkflowPanelOpen(false);
    setDailyTasksPanelOpen(false);
    setContextChatOpen(false);
  };

  const handleViewWorkflow = (id) => {
    setSelectedWorkflowId(id);
    setActiveNav('workflow-detail');
    setWorkflowPanelOpen(false);
    setDailyTasksPanelOpen(false);
    setContextChatOpen(false);
  };

  const handleCreateWorkflow = () => {
    setActiveNav('workflow-builder');
    setWorkflowPanelOpen(false);
    setDailyTasksPanelOpen(false);
    setContextChatOpen(false);
  };

  const handleWorkflowBack = () => {
    setActiveNav('workflows');
    setSelectedWorkflowId(null);
  };

  const handleViewAgent = (id) => {
    setSelectedAgentId(id);
    setActiveNav('agent-detail');
    setWorkflowPanelOpen(false);
    setDailyTasksPanelOpen(false);
    setContextChatOpen(false);
  };

  const handleAgentBack = () => {
    setActiveNav('agents');
    setSelectedAgentId(null);
  };

  const showExecPanel = executionState?.active && !executionState?.inView && executions.length > 0;
  const currentExec = executions[activeExecIndex] || executions[0];

  // "New" dot on `?` button — shown for users who saw the tour but didn't complete it,
  // for up to 3 sessions. Auto-clears when they open the cheat sheet or replay the tour.
  const [showReplayCue, setShowReplayCue] = useState(false);
  useEffect(() => {
    try {
      const seen = localStorage.getItem('implify-tour-seen') === '1';
      const completed = localStorage.getItem('implify-tour-completed') === '1';
      if (!seen || completed) return;
      const views = parseInt(localStorage.getItem('implify-tour-cta-views') || '0', 10) || 0;
      if (views >= 3) return;
      localStorage.setItem('implify-tour-cta-views', String(views + 1));
      setShowReplayCue(true);
    } catch { /* noop */ }
  }, []);
  const dismissReplayCue = useCallback(() => {
    setShowReplayCue(false);
    try { localStorage.setItem('implify-tour-completed', '1'); } catch { /* noop */ }
  }, []);

  // Mark tour as completed when user reaches the final "Start exploring" CTA
  useEffect(() => {
    const handler = () => { trackEvent('tour_completed'); dismissReplayCue(); };
    window.addEventListener('implify:tour-completed', handler);
    return () => window.removeEventListener('implify:tour-completed', handler);
  }, [dismissReplayCue]);

  const menuItems = [
    { icon: Zap, label: 'Agent Hub' },
    { icon: Settings, label: 'Settings' },
    { icon: CreditCard, label: 'Billing' },
    { icon: Shield, label: 'Admin Console' },
    { icon: HelpCircle, label: 'Help & Support' },
  ];

  return (
    <Suspense fallback={null}>
    <div className={`${bg} fixed inset-0 overflow-hidden`} data-testid="v4-container">
      {/* Logo */}
      <div className="fixed top-5 left-5 z-40" data-testid="v4-logo">
        <img src={logo} alt="Implify" className="h-8" />
      </div>

      {/* Top-right panel */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-3" ref={menuRef}>
        {/* Standalone cheat-sheet ? button, sits OUTSIDE the glass panel on its left */}
        <div className="relative group" data-tour="cheatsheet-btn">
          <button
            onClick={() => { setCheatOpen(true); dismissReplayCue(); }}
            className={`w-10 h-10 rounded-xl flex items-center justify-center text-[13px] font-semibold transition-colors border backdrop-blur-md ${isDark ? 'bg-white/[0.05] text-white/85 border-white/[0.10] hover:bg-white/[0.12] hover:text-white' : 'bg-white/70 text-slate-700 border-black/[0.08] hover:bg-white/90 hover:text-slate-900'}`}
            data-testid="v4-cheat-sheet-btn"
            aria-label="Keyboard shortcuts"
          >
            ?
          </button>
          {showReplayCue && (
            <span
              className="absolute -top-1 -right-1 pointer-events-none"
              data-testid="v4-cheat-sheet-newdot"
              aria-hidden="true"
            >
              <span className="absolute inset-0 rounded-full bg-indigo-400/60 animate-ping" />
              <span className="relative block w-2.5 h-2.5 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 ring-2 ring-[#0a1628]/0" />
            </span>
          )}
          <div className={`absolute top-full right-0 mt-2 px-2.5 py-1.5 rounded-lg text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity ${isDark ? 'bg-white/[0.08] text-white/80 border border-white/[0.08]' : 'bg-white text-slate-700 border border-black/[0.08] shadow-md'}`}>
            Shortcuts · press <span className="font-semibold">?</span>
          </div>
        </div>

        <div
          className={`${glass} rounded-xl flex items-center gap-3 px-4`}
          style={{ height: '40px' }}
          data-testid="v4-top-right-panel"
        >
          <span className={`text-[11px] font-mono ${textPrimary}`} data-testid="v4-datetime-label">{simClock.dateStr} · {simClock.timeStr}</span>
          <div className={`h-4 w-px ${divider}`} />
          <Moon
            className={`w-4 h-4 cursor-pointer transition-colors ${isDark ? 'text-white' : iconColor}`}
            onClick={() => { setIsDark(true); localStorage.setItem('implify-theme', 'dark'); }}
            data-testid="v4-dark-mode-btn"
          />
          <Sun
            className={`w-4 h-4 cursor-pointer transition-colors ${!isDark ? 'text-slate-800' : iconColor}`}
            onClick={() => { setIsDark(false); localStorage.setItem('implify-theme', 'light'); }}
            data-testid="v4-light-mode-btn"
          />
          <div className="relative" data-tour="bell">
            {(() => {
              const activeAcct = activeUser?.id === 'ceo' ? 'arjun' : activeUser?.id === 'marketing' ? 'priya' : activeUser?.id === 'sales' ? 'david' : activeUser?.id;
              const visibleNotifs = (sim.notifications || []).filter(n => !n.toAccount || n.toAccount === activeAcct);
              const rippleUnread = visibleNotifs.filter(n => n.toAccount === activeAcct && !(n.readBy || []).includes(activeAcct)).length;
              const totalBadge = rippleUnread + (sim.unreadCount > 0 && visibleNotifs.some(n => !n.toAccount) ? sim.unreadCount : 0);
              const showBadge = totalBadge > 0 || executionState?.hitlPending;
              return (
                <>
                  <Bell
                    className={`w-4 h-4 cursor-pointer transition-colors ${iconColor}`}
                    onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen) { sim.markAllRead(); sim.markRippleNotifsRead?.(activeAcct); } setMenuOpen(false); setAccountsExpanded(false); }}
                    data-testid="v4-notifications-btn"
                  />
                  {showBadge && (
                    <div className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 rounded-full bg-red-500 flex items-center justify-center px-1 pointer-events-none" style={{ fontSize: 0 }}>
                      <span className="text-white text-[8px] font-bold leading-none">{totalBadge || '!'}</span>
                    </div>
                  )}
                  {notifOpen && <NotificationDropdown notifications={visibleNotifs} isDark={isDark} onClose={() => setNotifOpen(false)} />}
                </>
              );
            })()}
          </div>
          <div
            className={`flex items-center gap-2 cursor-pointer pl-2 border-l ${divider}`}
            onClick={() => { setMenuOpen(!menuOpen); setAccountsExpanded(false); setNotifOpen(false); }}
            data-testid="v4-profile-trigger"
            data-tour-group="persona-switcher"
          >
            <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${activeUser.gradient} flex items-center justify-center`}>
              <span className="text-white text-[10px] font-semibold">{activeUser.initials}</span>
            </div>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${menuOpen ? 'rotate-180' : ''} ${textSecondary}`} />
          </div>
        </div>

        {/* Dropdown Menu */}
        {menuOpen && (
          <div
            className={`${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'} absolute top-12 right-0 rounded-2xl py-2`}
            style={{ width: '240px' }}
            data-testid="v4-user-menu"
            data-tour-group="persona-switcher"
          >
            <div
              className={`px-4 py-3 flex items-center justify-between cursor-pointer rounded-lg mx-2 transition-colors ${hoverBg}`}
              onClick={() => setAccountsExpanded(!accountsExpanded)}
              data-testid="v4-account-toggle"
            >
              <div>
                <p className={`text-sm font-semibold ${textPrimary}`}>{activeUser.name}</p>
                <p className={`text-xs ${textSecondary}`}>{activeUser.role}</p>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform ${accountsExpanded ? 'rotate-180' : ''} ${textSecondary}`} />
            </div>

            {accountsExpanded && (
              <div className="px-3 py-1">
                {USERS.filter((_, idx) => idx !== userIndex).map((user) => (
                  <div
                    key={user.id}
                    className={`flex items-center gap-3 px-2 py-2 rounded-lg cursor-pointer transition-colors ${hoverBg}`}
                    onClick={() => switchAccount(USERS.indexOf(user))}
                    data-testid={`v4-switch-user-${user.id}`}
                  >
                    <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${user.gradient} flex items-center justify-center flex-shrink-0`}>
                      <span className="text-white text-[9px] font-semibold">{user.initials}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${textPrimary}`}>{user.name}</p>
                      <p className={`text-[11px] ${textSecondary}`}>{user.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className={`border-t ${divider} my-1`} />

            <div className="px-3 py-1">
              {menuItems.map((item) => (
                <div
                  key={item.label}
                  className={`flex items-center gap-3 px-2 py-2 rounded-lg cursor-pointer transition-colors ${hoverBg}`}
                  data-testid={`v4-menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className={`w-4 h-4 ${textSecondary}`} />
                  <span className={`text-sm ${textPrimary}`}>{item.label}</span>
                </div>
              ))}
            </div>

            <div className={`border-t ${divider} my-1`} />

            <div className="px-3 py-1">
              <div
                className="flex items-center gap-3 px-2 py-2 rounded-lg cursor-pointer transition-colors hover:bg-red-500/10"
                data-testid="v4-menu-logout"
              >
                <LogOut className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-400">Log out</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Execution Panel — sits above main content, aligned with nav top */}
      {showExecPanel && (
        <div
          className={`fixed z-30 transition-all duration-300`}
          style={{ left: navCollapsed ? 'calc(60px + 24px)' : 'calc(220px + 24px)', right: (workflowPanelOpen || dailyTasksPanelOpen || contextChatOpen) ? 'calc(280px + 24px)' : '12px', top: '80px' }}
          data-testid="execution-status-bar"
        >
          <div
            onClick={handleStatusBarClick}
            className={`cursor-pointer rounded-2xl px-4 py-3 flex items-center gap-3 transition-colors ${isDark ? 'bg-[#0b1220]/90 border border-white/[0.06] hover:border-indigo-500/30' : 'bg-white/90 border border-black/[0.06] hover:border-indigo-400/40'} backdrop-blur-xl`}
          >
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${currentExec?.hitlPending ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400 animate-pulse'}`} />
            <span className={`text-sm font-medium ${textPrimary} truncate`}>{currentExec?.title}</span>
            {currentExec?.completedSteps != null && (
              <span className={`text-xs flex-shrink-0 ${textSecondary}`}>{currentExec.completedSteps}/{currentExec.totalSteps} milestones</span>
            )}
            {currentExec?.hitlPending && (
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 animate-pulse ${isDark ? 'bg-amber-400/15 text-amber-400' : 'bg-amber-600/10 text-amber-600'}`}>
                Action Required
              </span>
            )}
            <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
              {executions.length > 1 && (
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setActiveExecIndex(i => (i - 1 + executions.length) % executions.length); }}
                    className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isDark ? 'hover:bg-white/[0.08]' : 'hover:bg-black/[0.06]'}`}
                    data-testid="exec-panel-prev"
                  >
                    <ChevronLeft className={`w-3 h-3 ${textSecondary}`} />
                  </button>
                  <div className="flex items-center gap-1">
                    {executions.map((_, i) => (
                      <button
                        key={i}
                        onClick={(e) => { e.stopPropagation(); setActiveExecIndex(i); }}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${i === activeExecIndex ? (isDark ? 'bg-indigo-400' : 'bg-indigo-500') : (isDark ? 'bg-white/20' : 'bg-black/15')}`}
                        data-testid={`exec-panel-dot-${i}`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setActiveExecIndex(i => (i + 1) % executions.length); }}
                    className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isDark ? 'hover:bg-white/[0.08]' : 'hover:bg-black/[0.06]'}`}
                    data-testid="exec-panel-next"
                  >
                    <ChevronRight className={`w-3 h-3 ${textSecondary}`} />
                  </button>
                </div>
              )}
              <span className={`text-[10px] font-medium ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>Click to view</span>
            </div>
          </div>
        </div>
      )}

      {/* Left Navigation Panel */}
      <div
        className={`${glass} fixed left-3 bottom-3 rounded-2xl z-20 overflow-hidden transition-all duration-300`}
        style={{ width: navCollapsed ? '60px' : '220px', top: '80px' }}
        data-testid="v4-left-panel"
      >
        <V4Sidebar isDark={isDark} activeNav={activeNav} onNavChange={handleNavChange} collapsed={navCollapsed} onToggleCollapse={() => setNavCollapsed(!navCollapsed)} activeUser={activeUser} />
      </div>

      {/* Main Content Panel */}
      <div
        className={`${glass} fixed bottom-3 rounded-2xl z-20 overflow-hidden transition-all duration-300`}
        style={{ left: navCollapsed ? 'calc(60px + 24px)' : 'calc(220px + 24px)', top: showExecPanel ? '140px' : '80px', right: (kvPanelOpen || intPanelOpen) ? 'calc(350px + 24px)' : (workflowPanelOpen || dailyTasksPanelOpen || contextChatOpen) ? 'calc(280px + 24px)' : '12px' }}
        data-testid="v4-chat-panel"
      >
        {activeNav === 'home' && !adHocScenario && !activeWorkflowExec && (
          <V4ChatPanel
            isDark={isDark}
            onWorkflowToggle={toggleWorkflow}
            workflowPanelOpen={workflowPanelOpen}
            onDailyTasksToggle={toggleDailyTasks}
            dailyTasksPanelOpen={dailyTasksPanelOpen}
            activeUser={activeUser}
            simState={sim}
            simClock={simClock}
          />
        )}
        {activeNav === 'home' && adHocScenario && (
          <V4AdHocChat
            isDark={isDark}
            scenarioId={adHocScenario}
            scenarios={activeUser?.id === 'marketing' ? PRIYA_HOME_SCENARIOS : activeUser?.id === 'sales' ? DAVID_HOME_SCENARIOS : undefined}
            backLabel={activeUser?.id === 'marketing' ? 'Back to Marketing' : activeUser?.id === 'sales' ? 'Back to Sales' : undefined}
            onBack={() => setAdHocScenario(null)}
            skipFirstUserDelay
          />
        )}
        {activeNav === 'home' && activeWorkflowExec && !adHocScenario && (
          <V4WorkflowExecution
            isDark={isDark}
            workflowId={activeWorkflowExec}
            activeAccount={activeUser?.id}
            onBack={() => setActiveWorkflowExec(null)}
          />
        )}
        {activeNav === 'org-pulse' && (
          <V4OrgPulse isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} onExecutionChange={handleExecutionChange} shouldResumeExecution={shouldResumeExecution} onResumeHandled={() => setShouldResumeExecution(false)} />
        )}
        {activeNav === 'history' && (
          <V4History isDark={isDark} activeUser={activeUser} />
        )}
        {activeNav === 'integrations' && (
          <V4Integrations isDark={isDark} activeUser={activeUser} onPanelChange={(open, content) => { setIntPanelOpen(open); setIntPanelContent(content); }} />
        )}
        {activeNav === 'knowledge-vault' && (
          <V4KnowledgeVault isDark={isDark} activeUser={activeUser} onPanelChange={(open, content) => { setKvPanelOpen(open); setKvPanelContent(content); }} />
        )}
        {activeNav === 'marketing' && !marketingAdHocScenario && (
          <V4MarketingDept isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} onToast={addToast} />
        )}
        {activeNav === 'marketing' && marketingAdHocScenario && (
          <V4AdHocChat
            isDark={isDark}
            scenarioId={marketingAdHocScenario}
            scenarios={MARKETING_SCENARIOS}
            backLabel="Back to Marketing"
            onBack={() => setMarketingAdHocScenario(null)}
            skipFirstUserDelay
          />
        )}
        {activeNav === 'mkt-campaigns' && !adHocScenario && (
          <V4MktCampaigns isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} priyaState={sim?.priyaState} />
        )}
        {activeNav === 'mkt-campaigns' && adHocScenario && activeUser?.id === 'marketing' && (
          <V4AdHocChat
            isDark={isDark}
            scenarioId={adHocScenario}
            scenarios={PRIYA_CAMPAIGN_PORTFOLIO_SCENARIOS}
            backLabel="Back to Campaigns"
            onBack={() => setAdHocScenario(null)}
            skipFirstUserDelay
          />
        )}
        {activeNav === 'mkt-social' && (
          <V4MktSocial
            isDark={isDark}
            onWorkflowToggle={toggleWorkflow}
            workflowPanelOpen={workflowPanelOpen}
            onNavigate={handleNavigateWithContext}
            activeUser={activeUser}
            priyaState={sim?.priyaState}
            socialAdHocScenario={activeUser?.id === 'marketing' ? adHocScenario : null}
            onSocialAdHocDone={() => setAdHocScenario(null)}
          />
        )}
        {activeNav === 'mkt-content' && (
          <V4MktContent
            isDark={isDark}
            onWorkflowToggle={toggleWorkflow}
            workflowPanelOpen={workflowPanelOpen}
            activeUser={activeUser}
            priyaState={sim?.priyaState}
            contentAdHocScenario={activeUser?.id === 'marketing' ? adHocScenario : null}
            onContentAdHocDone={() => setAdHocScenario(null)}
          />
        )}
        {activeNav === 'mkt-analytics' && (
          <V4MktAnalytics
            isDark={isDark}
            onWorkflowToggle={toggleWorkflow}
            workflowPanelOpen={workflowPanelOpen}
            navContext={navContext}
            activeUser={activeUser}
            analyticsAdHocScenario={activeUser?.id === 'marketing' ? adHocScenario : null}
            onAnalyticsAdHocDone={() => setAdHocScenario(null)}
          />
        )}
        {activeNav === 'sales' && !salesAdHocScenario && (
          <V4SalesDept isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} onToast={addToast} />
        )}
        {activeNav === 'sales' && salesAdHocScenario && (
          <V4AdHocChat
            isDark={isDark}
            scenarioId={salesAdHocScenario}
            scenarios={SALES_SCENARIOS}
            backLabel="Back to Sales"
            onBack={() => setSalesAdHocScenario(null)}
            skipFirstUserDelay
          />
        )}
        {activeNav === 'sales-deals' && (
          <V4SalesDeals
            isDark={isDark}
            onWorkflowToggle={toggleWorkflow}
            workflowPanelOpen={workflowPanelOpen}
            activeUser={activeUser}
            davidState={sim?.davidState}
            dealAdHocScenario={activeUser?.id === 'sales' ? adHocScenario : null}
            onDealAdHocDone={() => setAdHocScenario(null)}
          />
        )}
        {activeNav === 'sales-pipeline' && (
          <V4SalesPipeline
            isDark={isDark}
            onWorkflowToggle={toggleWorkflow}
            workflowPanelOpen={workflowPanelOpen}
            activeUser={activeUser}
            pipelineAdHocScenario={activeUser?.id === 'sales' ? adHocScenario : null}
            onPipelineAdHocDone={() => setAdHocScenario(null)}
          />
        )}
        {activeNav === 'sales-outreach' && (
          <V4SalesOutreach
            isDark={isDark}
            onWorkflowToggle={toggleWorkflow}
            workflowPanelOpen={workflowPanelOpen}
            activeUser={activeUser}
            davidState={sim?.davidState}
            outreachAdHocScenario={activeUser?.id === 'sales' ? adHocScenario : null}
            onOutreachAdHocDone={() => setAdHocScenario(null)}
          />
        )}
        {activeNav === 'sales-reports' && (
          <V4SalesReports
            isDark={isDark}
            onWorkflowToggle={toggleWorkflow}
            workflowPanelOpen={workflowPanelOpen}
            activeUser={activeUser}
            reportsAdHocScenario={activeUser?.id === 'sales' ? adHocScenario : null}
            onReportsAdHocDone={() => setAdHocScenario(null)}
          />
        )}
        {activeNav === 'support' && !supportAdHocScenario && (
          <V4SupportDept isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'support' && supportAdHocScenario && (
          <V4AdHocChat
            isDark={isDark}
            scenarioId={supportAdHocScenario}
            scenarios={SUPPORT_SCENARIOS}
            backLabel="Back to Support"
            onBack={() => setSupportAdHocScenario(null)}
            skipFirstUserDelay
          />
        )}
        {activeNav === 'support-tickets' && (
          <V4SupportTickets isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'support-churn' && (
          <V4SupportChurn isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'support-sentiment' && (
          <V4SupportSentiment isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'people' && !peopleAdHocScenario && (
          <V4PeopleDept isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'people' && peopleAdHocScenario && (
          <V4AdHocChat
            isDark={isDark}
            scenarioId={peopleAdHocScenario}
            scenarios={PEOPLE_SCENARIOS}
            backLabel="Back to People"
            onBack={() => setPeopleAdHocScenario(null)}
            skipFirstUserDelay
          />
        )}
        {activeNav === 'people-team' && (
          <V4PeopleTeam isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'people-hiring' && (
          <V4PeopleHiring isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'people-engagement' && (
          <V4PeopleEngagement isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'people-org-health' && (
          <V4PeopleOrgHealth isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'finance' && !financeAdHocScenario && (
          <V4FinanceDept isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'finance' && financeAdHocScenario && (
          <V4AdHocChat
            isDark={isDark}
            scenarioId={financeAdHocScenario}
            scenarios={FINANCE_SCENARIOS}
            backLabel="Back to Finance"
            onBack={() => setFinanceAdHocScenario(null)}
            skipFirstUserDelay
          />
        )}
        {activeNav === 'finance-cashflow' && (
          <V4FinanceCashflow isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'finance-budgets' && (
          <V4FinanceBudgets isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'finance-invoices' && (
          <V4FinanceInvoices isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'finance-reports' && (
          <V4FinanceReports isDark={isDark} onWorkflowToggle={toggleWorkflow} workflowPanelOpen={workflowPanelOpen} />
        )}
        {activeNav === 'workflows' && (
          <V4WorkflowsList isDark={isDark} onViewWorkflow={handleViewWorkflow} onCreateWorkflow={handleCreateWorkflow} activeUser={activeUser} />
        )}
        {activeNav === 'workflow-detail' && selectedWorkflowId && (
          <V4WorkflowDetail isDark={isDark} workflowId={selectedWorkflowId} onBack={handleWorkflowBack} onEdit={() => {}} />
        )}
        {activeNav === 'workflow-builder' && (
          <V4WorkflowBuilder isDark={isDark} onBack={handleWorkflowBack} onSave={(mode) => { handleWorkflowBack(); }} />
        )}
        {activeNav === 'agents' && (
          <V4AgentsList isDark={isDark} activeUser={activeUser} onViewAgent={handleViewAgent} />
        )}
        {activeNav === 'agent-detail' && selectedAgentId && (
          <V4AgentDetail isDark={isDark} agentId={selectedAgentId} activeUser={activeUser} onBack={handleAgentBack} />
        )}
      </div>

      {/* Right Panel — Workflow, Daily Tasks, Context Chat, or KV Panel */}
      <div
        className={`${glass} fixed bottom-3 right-3 rounded-2xl z-20 overflow-hidden transition-all duration-300`}
        style={{
          width: (kvPanelOpen || intPanelOpen) ? '350px' : '280px',
          top: '80px',
          opacity: (workflowPanelOpen || dailyTasksPanelOpen || contextChatOpen || kvPanelOpen || intPanelOpen) ? 1 : 0,
          pointerEvents: (workflowPanelOpen || dailyTasksPanelOpen || contextChatOpen || kvPanelOpen || intPanelOpen) ? 'auto' : 'none',
          transform: (workflowPanelOpen || dailyTasksPanelOpen || contextChatOpen || kvPanelOpen || intPanelOpen) ? 'translateX(0)' : 'translateX(20px)',
        }}
        data-testid="v4-right-panel"
      >
        {workflowPanelOpen && <WorkflowColumn isDark={isDark} onClose={() => setWorkflowPanelOpen(false)} agentFilter={activeNav === 'org-pulse' ? 'Stark' : null} activeAccount={activeUser?.id} onUse={(id) => { trackEvent('workflow_used', { workflow_id: id, persona: activeUser?.id }); setActiveWorkflowExec(id); setAdHocScenario(null); setWorkflowPanelOpen(false); }} />}
        {dailyTasksPanelOpen && <V4DailyTasksPanel isDark={isDark} onClose={() => setDailyTasksPanelOpen(false)} activeUser={activeUser} />}
        {contextChatOpen && <ContextChatPanel isDark={isDark} agentId={contextChatAgent} messages={contextChatMessages} onClose={() => setContextChatOpen(false)} onSend={(msg) => handleContextChat(msg, contextChatAgent)} />}
        {kvPanelOpen && kvPanelContent}
        {intPanelOpen && intPanelContent}
      </div>

      {/* Toast System */}
      <ToastSystem toasts={toasts} onDismiss={dismissToast} />

      {/* Keyboard shortcut cheat sheet */}
      <V4CheatSheet isDark={isDark} open={cheatOpen} onClose={() => setCheatOpen(false)} activeNav={activeNav} activeUserId={activeUser?.id} />
      <V4OnboardingTour isDark={isDark} open={tourOpen} onClose={() => setTourOpen(false)} />
    </div>
    </Suspense>
  );
};

const V4Wrapper = () => (
  <ChatInputRegistryProvider>
    <V4WrapperInner />
  </ChatInputRegistryProvider>
);

export default V4Wrapper;
